import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Box, Button, Card, CircularProgress, Grid, Link, makeStyles } from '@material-ui/core'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory, useParams } from 'react-router-dom'
import { Formik, Form } from 'formik'
import ViewHeader from 'components/ViewHeader'
import ProgressButton from 'components/ProgressButton'
import { fetchSpeakerById, resetSpeaker } from 'store/speaker'
import { addSpeaker, updateSpeakerById, updateSpeakerBio } from 'store/speaker'
import CustomTextField from 'components/Form/CustomTextField'
import { Collection, organizationStatus, SPEAKER_INFO_TEXT, SpeakerRoles } from 'constants/index'
import { speakerFormSchema } from 'schemas/speaker'
import { toast } from 'utils/notify'
import PollingListing from 'views/admin/subjects/PollingForm/PollingListing'
import pollingSlice from 'store/polling'
import { omit } from 'lodash'
import AvatarPicker from 'components/AvatarPicker'
import { uploadImageFirstTime } from 'utils'
import TipTapEditor from 'components/TipTapEditor/TipTapEditor'
import ErrorMessage from 'components/ErrorMessage'
import InfoIconAlongText from 'components/InfoIconAlongText'
import CustomCheckboxGroup from 'components/Form/CustomCheckboxGroup'
import moment from 'moment'
import { summarizeContent, SummarizationContentType } from 'utils/groq'
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined'
import RestartAltOutlinedIcon from '@mui/icons-material/RestartAltOutlined'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'
import ConfirmModal from 'components/ConfirmationModal'

const SpeakerForm = ({ preDefinedRole, displayContext, onSuccess, onCloseModal }) => {
  const classes = useStyles()
  const { speakerId } = useParams()
  const dispatch = useDispatch()
  const history = useHistory()
  const [profileImage, setProfileImage] = useState(null)
  const [resetImage, setResetImage] = useState(false)
  const [loading, setLoading] = useState(false)
  const [summarizing, setSummarizing] = useState(false)
  const [reverting, setReverting] = useState(false)
  const [viewingOriginal, setViewingOriginal] = useState(false)
  const [confirmResummarize, setConfirmResummarize] = useState(false)
  const createNewRef = useRef(false)

  const { user } = useSelector((state) => state.auth)
  const { speaker } = useSelector((state) => state.speaker)
  const { conference } = useSelector((state) => state.conference)

  const initialValues = {
    firstName: speaker?.firstName ?? '',
    lastName: speaker?.lastName ?? '',
    email: speaker?.email ?? '',
    company: speaker?.company ?? '',
    position: speaker?.position ?? '',
    bio: speaker?.bio ?? '',
    originalBio: speaker?.originalBio ?? '',
    websiteUrl: speaker?.websiteUrl ?? '',
    roles: speaker?.roles ? speaker.roles : preDefinedRole ? [preDefinedRole] : [],

    credential: speaker?.credential ?? '',
    phone: speaker?.phone ?? '',
    city: speaker?.city ?? '',
    state: speaker?.state ?? '',
    country: speaker?.country ?? ''
  }

  const speakerRoleOptions = [
    { value: SpeakerRoles.PRESENTER, label: 'Presenter' },
    { value: SpeakerRoles.MODERATOR, label: 'Moderator' }
  ]

  const forApproval = useMemo(
    () => speakerId === speaker?.id && speaker?.status === organizationStatus.PENDING_DATA_REVIEW,
    [speaker]
  )

  const handleImageChange = (file) => {
    setProfileImage(file)
  }

  const goBack = () => {
    dispatch(resetSpeaker())
    dispatch(pollingSlice.actions.setPolls([]))
    history.goBack()
  }

  useEffect(() => {
    if (speakerId) {
      dispatch(fetchSpeakerById(speakerId))
    }
    return () => dispatch(resetSpeaker())
  }, [dispatch, speakerId])

  const handleUpload = async (name) => {
    const modifiedSpeaker = omit(speaker, 'id')
    await dispatch(
      updateSpeakerById(speakerId, { ...modifiedSpeaker, imageName: name }, user?.conferenceId)
    )
  }

  const handleDelete = async () => {
    const modifiedSpeaker = omit(speaker, 'id')
    await dispatch(
      updateSpeakerById(speakerId, { ...modifiedSpeaker, imageName: null }, user?.conferenceId)
    )
  }

  const persistBio = async (bio, originalBio) => {
    await updateSpeakerBio(speakerId, bio, originalBio)
  }

  const handleSummarize = async (values, setFieldValue) => {
    setConfirmResummarize(false)
    setSummarizing(true)
    try {
      const originalBio = values.originalBio || values.bio
      const summary = await summarizeContent(originalBio, SummarizationContentType.SPEAKER_BIO)
      if (summary) {
        setFieldValue('originalBio', originalBio)
        setFieldValue('bio', summary)
        if (speakerId) {
          await persistBio(summary, originalBio)
          toast.success('Bio summarized and saved.')
        } else {
          toast.success('Bio summarized.')
        }
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to summarize bio.')
    } finally {
      setSummarizing(false)
    }
  }

  const handleRevertToOriginal = async (values, setFieldValue) => {
    setReverting(true)
    try {
      setFieldValue('bio', values.originalBio)
      setFieldValue('originalBio', '')
      setViewingOriginal(false)
      if (speakerId) {
        await persistBio(values.originalBio, '')
        toast.success('Bio reverted to original and saved.')
      } else {
        toast.success('Bio reverted to original.')
      }
    } finally {
      setReverting(false)
    }
  }

  const onCancel = () => {
    if (displayContext === 'page') {
      goBack()
    } else {
      onCloseModal()
    }
  }

  const onSubmit = async (values) => {
    setLoading(true)
    const payload = {
      firstName: values.firstName,
      lastName: values.lastName,
      fullName: `${values.firstName} ${values.lastName}`,
      email: values.email,
      bio: values.bio,
      originalBio: values.originalBio ?? '',
      company: values.company,
      position: values.position,
      websiteUrl: values.websiteUrl,
      roles: values.roles,

      credential: values?.credential ?? '',
      phone: values?.phone ?? '',
      city: values?.city ?? '',
      state: values?.state ?? '',
      country: values?.country ?? ''
    }
    if (profileImage) {
      payload.imageName = await uploadImageFirstTime(
        profileImage,
        Collection.SPEAKERS,
        user?.conferenceId
      )
    }

    let res, message
    if (speakerId) {
      if (forApproval) {
        payload.status = organizationStatus.APPROVED
        payload.approvedAt = moment().utc().valueOf()
        message = 'Speaker approved successfully.'
      } else {
        message = 'Speaker updated successfully.'
      }
      res = await dispatch(updateSpeakerById(speakerId, payload, user?.conferenceId))
    } else {
      res = await dispatch(addSpeaker(payload, user?.conferenceId))
      message = 'Speaker added successfully.'
    }
    setLoading(false)
    setProfileImage(null)
    if (res && displayContext === 'page') {
      toast.success(message)
      if (!createNewRef.current) {
        goBack()
      }
    }
    displayContext === 'modal' && onSuccess && onSuccess(res)
  }

  return (
    <Card
      elevation={displayContext === 'page' ? 3 : 0}
      className={displayContext === 'page' ? classes.card : classes.styledCard}>
      <Formik
        initialValues={initialValues}
        enableReinitialize
        onSubmit={onSubmit}
        validationSchema={speakerFormSchema}
        validateOnBlur={true}
        validateOnChange={true}
        validateOnMount={true}>
        {({
          isValid,
          values,
          touched,
          errors,
          setFieldValue,
          setFieldTouched,
          submitForm,
          resetForm,
          validateForm
        }) => (
          <Form>
            {displayContext === 'page' && (
              <ViewHeader
                title={
                  forApproval
                    ? 'Review & Approve Speaker'
                    : speakerId
                    ? 'Edit Speaker'
                    : 'Create Speaker'
                }>
                <Box style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <Button variant="outlined" onClick={goBack}>
                    Back
                  </Button>
                  <ProgressButton
                    type="button"
                    label={forApproval ? 'Save & Approve' : 'Save'}
                    size="small"
                    inProgress={loading}
                    disabled={loading}
                    onClick={async () => {
                      createNewRef.current = false
                      validateForm()
                      await submitForm()
                    }}
                  />
                  {!forApproval && !speakerId && displayContext === 'page' && (
                    <ProgressButton
                      type="button"
                      label="Save and Create New"
                      size="small"
                      inProgress={loading}
                      disabled={loading}
                      onClick={async () => {
                        createNewRef.current = true
                        validateForm()
                        await submitForm()
                        if (isValid) {
                          resetForm()
                          setResetImage(true)
                          history.replace(`/admin/people/speakers/new`)
                        }
                        createNewRef.current = false
                      }}
                    />
                  )}
                </Box>
              </ViewHeader>
            )}
            <Grid container spacing={3}>
              <Grid item xs={12} lg={4}>
                <CustomTextField label="First Name" name="firstName" type="text" />
              </Grid>
              <Grid item xs={12} lg={4}>
                <CustomTextField label="Last Name" name="lastName" type="text" />
              </Grid>
              <Grid item xs={12} lg={4}>
                <CustomTextField label="Email" name="email" type="email" disabled={!!speakerId} />
              </Grid>
              <Grid item xs={12} lg={4}>
                <CustomTextField label="Credential" name="credential" type="text" />
              </Grid>
              <Grid item xs={12} lg={4}>
                <CustomTextField label="Position/Title" name="position" type="text" />
              </Grid>
              <Grid item xs={12} lg={4}>
                <CustomTextField label="Organization" name="company" type="text" />
              </Grid>

              <Grid item xs={12} md={4}>
                <CustomTextField label="City" name="city" type="text" />
              </Grid>

              <Grid item xs={12} md={4}>
                <CustomTextField label="State" name="state" type="text" />
              </Grid>

              <Grid item xs={12} md={4}>
                <CustomTextField label="Country" name="country" type="text" />
              </Grid>

              <Grid item xs={12} md={4}>
                <CustomTextField label="Phone" name="phone" type="text" />
              </Grid>

              <Grid item xs={12} lg={4}>
                <CustomTextField label="Website URL" name="websiteUrl" type="text" />
              </Grid>
              <Grid item xs={12} lg={4}>
                <Box
                  style={{
                    display: 'flex',
                    flexDirection: 'column'
                  }}>
                  <label className={classes.label}>
                    Roles
                    <InfoIconAlongText infoText={SPEAKER_INFO_TEXT.roles} />
                  </label>
                  <Box style={{ marginLeft: '2px' }}>
                    <CustomCheckboxGroup
                      name="roles"
                      options={speakerRoleOptions}
                      labelField="label"
                      valueField="value"
                      disabled={!!preDefinedRole}
                    />
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12} lg={12}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                  <Box
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}>
                    <label className={classes.label}>
                      Bio
                      <InfoIconAlongText infoText={SPEAKER_INFO_TEXT.bio} />
                    </label>
                    <Box style={{ display: 'flex', gap: 8 }}>
                      {values.bio?.replace(/<[^>]*>/g, '').trim() && (
                        <Button
                          type="button"
                          size="small"
                          variant="outlined"
                          color="primary"
                          startIcon={
                            summarizing || reverting ? (
                              <CircularProgress size={14} color="inherit" />
                            ) : (
                              <AutoAwesomeOutlinedIcon />
                            )
                          }
                          disabled={summarizing || reverting}
                          onClick={() => {
                            if (values.originalBio) {
                              setConfirmResummarize(true)
                            } else {
                              handleSummarize(values, setFieldValue)
                            }
                          }}>
                          {reverting
                            ? 'Reverting...'
                            : summarizing
                            ? 'Summarizing...'
                            : values.originalBio
                            ? 'Re-summarize'
                            : 'Summarize with AI'}
                        </Button>
                      )}
                    </Box>
                  </Box>
                  <TipTapEditor
                    value={values.bio}
                    onChange={(value) => setFieldValue('bio', value)}
                    placeholder="Write bio here..."
                    error={!!(touched?.bio && errors?.bio)}
                    onBlur={() => setFieldTouched('bio', true)}
                    minHeight="150px"
                    maxHeight="400px"
                    hasPlaceholders={false}
                  />
                  <ErrorMessage message={touched?.bio && errors?.bio} />
                  {values.originalBio && (
                    <Box style={{ display: 'flex', gap: 16, marginTop: 4 }}>
                      <Link
                        component="button"
                        type="button"
                        variant="body2"
                        className={classes.linkButton}
                        disabled={summarizing || reverting}
                        onClick={() => setViewingOriginal((prev) => !prev)}>
                        <VisibilityOutlinedIcon fontSize="inherit" className={classes.linkIcon} />
                        {viewingOriginal ? 'Hide Original' : 'View Original'}
                      </Link>
                      <Link
                        component="button"
                        type="button"
                        variant="body2"
                        className={classes.linkButton}
                        disabled={summarizing || reverting}
                        onClick={() => handleRevertToOriginal(values, setFieldValue)}>
                        {reverting ? (
                          <CircularProgress size={12} color="inherit" />
                        ) : (
                          <RestartAltOutlinedIcon fontSize="inherit" className={classes.linkIcon} />
                        )}
                        {reverting ? 'Reverting...' : 'Undo AI'}
                      </Link>
                    </Box>
                  )}
                  {viewingOriginal && (
                    <Box className={classes.originalTextBox}>
                      <Box className={classes.originalTextLabel}>ORIGINAL — READ ONLY</Box>
                      <Box
                        className={classes.originalTextContent}
                        dangerouslySetInnerHTML={{ __html: values.originalBio }}
                      />
                    </Box>
                  )}
                  <ConfirmModal
                    dialogTitle="Summarize Bio"
                    dialogText="This bio has already been summarized. Do you want to summarize it again?"
                    open={confirmResummarize}
                    closeModal={() => setConfirmResummarize(false)}
                    handleConfirm={() => handleSummarize(values, setFieldValue)}
                  />
                </Box>
              </Grid>

              {conference?.havePolls && speakerId && (
                <Grid item xs={12} md={8}>
                  <PollingListing speakerId={speakerId} forApproval={false} />
                </Grid>
              )}

              <Grid item xs={12} lg={6}>
                <AvatarPicker
                  title="Profile Picture"
                  subTitle="Allowed Format: PNG/JPG/JPEG; Dimensions: 320x320"
                  path={Collection.SPEAKERS}
                  handleCustomChange={handleImageChange}
                  onUpload={handleUpload}
                  onDelete={handleDelete}
                  imageName={speakerId ? speaker?.imageName : null}
                  resetImage={resetImage}
                  conferenceId={user?.conferenceId}
                  infoText={SPEAKER_INFO_TEXT.profile}
                  firsTimeUpload={!speakerId}
                  cropOnSelect={true}
                  imageProps={{ style: { objectFit: 'cover', objectPosition: '50% 12%' } }}
                />
              </Grid>
            </Grid>
            <Grid container justifyContent="flex-end" spacing={2}>
              <Grid item>
                <Button size="large" disabled={loading} onClick={onCancel}>
                  Cancel
                </Button>
              </Grid>
              <Grid item>
                <ProgressButton
                  type="button"
                  label={forApproval ? 'Save & Approve' : 'Save'}
                  inProgress={loading}
                  disabled={loading}
                  onClick={async () => {
                    createNewRef.current = false
                    validateForm()
                    await submitForm()
                  }}
                />
              </Grid>
              {!forApproval && !speakerId && displayContext === 'page' && (
                <Grid item>
                  <ProgressButton
                    type="button"
                    label="Save and Create New"
                    inProgress={loading}
                    disabled={loading}
                    onClick={async () => {
                      createNewRef.current = true
                      validateForm()
                      await submitForm()
                      if (isValid) {
                        resetForm()
                        setResetImage(true)
                        history.replace(`/admin/people/speakers/new`)
                      }
                      createNewRef.current = false
                    }}
                  />
                </Grid>
              )}
            </Grid>
          </Form>
        )}
      </Formik>
    </Card>
  )
}
export default SpeakerForm

const useStyles = makeStyles({
  label: {
    fontSize: '13px',
    letterSpacing: '0.5px',
    color: '#606060',
    display: 'flex',
    alignItems: 'center',
    gap: '0.4rem',
    marginLeft: '0.2rem'
  },
  card: {
    padding: '24px',
    overflow: 'visible'
  },
  styledCard: {
    paddingTop: '1rem'
  },
  linkButton: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    fontSize: '13px',
    textTransform: 'none',
    cursor: 'pointer',
    background: 'none',
    border: 'none',
    padding: 0,
    '&:hover': {
      textDecoration: 'underline',
      background: 'none'
    },
    '&:disabled': {
      cursor: 'default',
      opacity: 0.5
    }
  },
  linkIcon: {
    fontSize: '15px'
  },
  originalTextBox: {
    marginTop: '0.5rem',
    padding: '12px',
    borderRadius: '4px',
    border: '1px solid #e0e0e0',
    backgroundColor: '#fafafa'
  },
  originalTextLabel: {
    fontSize: '11px',
    letterSpacing: '0.5px',
    color: '#9e9e9e',
    marginBottom: '6px'
  },
  originalTextContent: {
    fontSize: '14px',
    color: '#606060'
  }
})
